import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { faEye, faFilePdf } from "@fortawesome/free-solid-svg-icons";
import { faPencil, faRotateReverse } from "@fortawesome/pro-duotone-svg-icons";
import {
  faFileInvoiceDollar,
  faFiles,
} from "@fortawesome/pro-regular-svg-icons";
import { faFunnelDollar } from "@fortawesome/pro-solid-svg-icons";
import { Box } from "@material-ui/core";
import { useFormikContext } from "formik";
import { kebabCase } from "lodash";
import { useDispatch, useSelector } from "react-redux";

import {
  BillingDocumentAction,
  BillingRunAction,
  PaymentAction,
  SaleAction,
  openDispatchingModal,
  setSetting,
} from "actions";

import { IntegrationCredentialAction } from "actions/integrationCredentials";
import { openGenerateReportsFromBusinessTransactionsTable } from "actions/modals/business";

import AgGridTable, {
  globalDefaultColDef,
} from "components/AgGrid/AgGridContainer";
import { RowSelectionMode } from "components/AgGrid/constants";
import { LoadAll } from "components/AgGrid/LoadAll";
import BulkUpdateBillingDocumentModal from "components/BulkUpdateOptionalFieldsModal/BillingDocuments";
import { Column } from "components/Layout";
import WaitForSync from "components/LoadingSpinner/WaitForSync";
import { useGetDownloadOutstandingSettlementReport } from "components/MoneyTable/outstandingSettlementReport";
import { GenerateMode } from "components/MultiSaleReports";
import { TitleText } from "components/Text";
import { TimeSince } from "components/TimeSince";

import { AgGridTables } from "constants/aggrid";
import { IntegrationType } from "constants/integrations";
import { ApiModel } from "constants/loading";
import { ModalTypes } from "constants/navigation";
import { Reports } from "constants/reports";
import { DeploymentPermission } from "constants/permissions";
import { Settings } from "constants/settings";

import { firstIfAllEqual } from "lib";

import { formatISO8601DateString } from "lib/timeFormats";

import { EmailDetailGridOptions } from "lib/agGrid/columnDefinitions/email";
import { openModalLink } from "lib/navigation";
import toast from "lib/toast";

import {
  getBusinessById,
  getDefaultBillingSettings,
  getActiveRole,
  getBillingDocuments,
  getIntegrationCredentialById,
  getIntegrationCredentialIdsByType,
  getIsFetchingBillingDocuments,
  getIsStaff,
  getProperties,
  getSetting,
  selectBillingDocumentsAggridData,
} from "selectors";

import { useBoolean, useHasDeploymentPermission, useMountEffect } from "hooks";
import { takeFormikSnapshot } from "sagas/modals/util";

import { MoneyColumnDefinitions } from "./columnDefinitions";

const getRowId = params => params.data.billingDocument.id;

const detailCellRendererParams = {
  detailGridOptions: EmailDetailGridOptions,
  getDetailRowData: params => {
    params.successCallback(params.data.emails);
  },
};

const defaultColDef = {
  ...globalDefaultColDef,
  enableCellChangeFlash: false,
};

export const MoneyTable = ({
  businessId,
  allDocumentsLoaded,
  setAllDocumentsLoaded,
  tableName = AgGridTables.MONEY,
}) => {
  const [selectedBillingDocumentIds, setSelectedBillingDocumentIds] = useState(
    [],
  );
  const propertyByIdLookup = useSelector(getProperties);
  const userRoleSlug = useSelector(state => getActiveRole(state).slug);
  const isDocumentsFetching = useSelector(getIsFetchingBillingDocuments);

  const billingDocumentByIdLookup = useSelector(getBillingDocuments);

  const billingDocumentRowData = useSelector(selectBillingDocumentsAggridData);

  const business = useSelector(getBusinessById(businessId));
  const businessName = business?.name;

  const rowData = useMemo(() => {
    if (businessId) {
      return billingDocumentRowData.filter(
        billingDocumentRow => billingDocumentRow.business.id === businessId,
      );
    }
    return billingDocumentRowData;
  }, [billingDocumentRowData, businessId]);

  const formikProps = useFormikContext();

  const context = {
    propertyByIdLookup,
    userRoleSlug,
  };

  const dispatch = useDispatch();
  const isStaff = useSelector(getIsStaff);

  const xeroCredentialIds = useSelector(
    getIntegrationCredentialIdsByType(IntegrationType.Xero),
  );

  const xeroCredentialId = xeroCredentialIds?.[0];

  const integrationCredential = useSelector(
    getIntegrationCredentialById(xeroCredentialId),
  );

  const lastSync = useMemo(
    () =>
      integrationCredential ? (
        <div>
          <i>
            Integration synced:{" "}
            {integrationCredential.integrationDocumentsLastSyncedAt
              ? TimeSince({
                  utcDateTimeStr:
                    integrationCredential.integrationDocumentsLastSyncedAt,
                })
              : "never"}
          </i>
        </div>
      ) : null,
    [integrationCredential],
  );

  const syncXeroDocuments = useCallback(
    () => dispatch(IntegrationCredentialAction.syncDocuments(xeroCredentialId)),
    [dispatch, xeroCredentialId],
  );

  const syncingDocumentToastText = "Fetching All Documents";

  const loadAllDocuments = useCallback(() => {
    dispatch(BillingDocumentAction.request());
    dispatch(PaymentAction.request());
    toast.syncing(syncingDocumentToastText, {
      autoClose: false,
    });
    setAllDocumentsLoaded(true);
  }, [dispatch, setAllDocumentsLoaded]);

  useEffect(() => {
    if (!isDocumentsFetching) {
      toast.dismiss(kebabCase(syncingDocumentToastText));
    }
  }, [isDocumentsFetching]);

  const loadAllLabel = useMemo(() => {
    if (businessId) {
      return null;
    }
    return (
      <LoadAll
        allLoaded={allDocumentsLoaded}
        onClick={loadAllDocuments}
        text="Only unpaid documents and those modified within 45 days are shown."
      />
    );
  }, [businessId, allDocumentsLoaded, loadAllDocuments]);

  const [
    isEditBillingDocumentsOpen,
    openEditBillingDocuments,
    closeEditBillingDocuments,
  ] = useBoolean(false);

  const selectedBillingDocuments = useMemo(
    () =>
      selectedBillingDocumentIds.map(
        billingDocumentId => billingDocumentByIdLookup[billingDocumentId],
      ),

    [billingDocumentByIdLookup, selectedBillingDocumentIds],
  );

  const bulkInitialValues = useMemo(
    () => ({
      status: firstIfAllEqual(
        selectedBillingDocuments.map(billingDocument => billingDocument.status),
        undefined,
      ),
      dueDate: firstIfAllEqual(
        selectedBillingDocuments.map(
          billingDocument => billingDocument.dueDate,
        ),
        undefined,
      ),
    }),
    [selectedBillingDocuments],
  );
  const selectedCount = selectedBillingDocumentIds.length;

  const [onGridReadyExtra, handleDownloadOutstandingSettlementReport] =
    useGetDownloadOutstandingSettlementReport();

  const agGridRef = useRef();

  const generateInterestCharges = useCallback(() => {
    if (!allDocumentsLoaded) {
      loadAllDocuments();
    }
    openModalLink(ModalTypes.Interest);
  }, [allDocumentsLoaded, loadAllDocuments]);

  const previewInterestCharges = useCallback(() => {
    dispatch(
      openDispatchingModal(
        ModalTypes.InterestPreview,
        window.location.hash,
        {},
      ),
    );
  }, [dispatch]);

  const onClickGenerateFinancialStatements = () => {
    openModalLink(ModalTypes.MultiSaleReports, {
      allowedReports: [
        Reports.FinancialStatementReport.slug,
        Reports.FinancialStatementZipReport.slug,
        Reports.DetailedFinancialStatementReport.slug,
        Reports.OutstandingStatementReport.slug,
      ],
      generateMode: GenerateMode.MANY,
    });
  };

  const onClickGenerateBusinessReports = useCallback(() => {
    dispatch(
      openGenerateReportsFromBusinessTransactionsTable(
        businessId,
        takeFormikSnapshot(formikProps),
      ),
    );
  }, [businessId, dispatch, formikProps]);

  function onGridReady(agGrid) {
    agGridRef.current = agGrid;
    onGridReadyExtra(agGrid);
  }

  const hasInterestFeature = useHasDeploymentPermission(
    DeploymentPermission.featureInterest,
  );

  const billingSettings = useSelector(getDefaultBillingSettings);
  const interestEnabled =
    !businessId &&
    hasInterestFeature &&
    billingSettings &&
    billingSettings.enableInterest;

  const compileSelected = useCallback(
    () =>
      dispatch(
        BillingDocumentAction.compileDocuments(selectedBillingDocumentIds),
      ),
    [dispatch, selectedBillingDocumentIds],
  );

  const additionalActions = useMemo(
    () =>
      [
        !businessId && {
          title: "Download Outstanding Settlement Report",
          icon: faFunnelDollar,
          onClick: handleDownloadOutstandingSettlementReport,
          dataTour: "outstandingSettlementReport",
        },
        integrationCredential && {
          title: "Sync Financial Integrations",
          icon: faRotateReverse,
          onClick: syncXeroDocuments,
          dataTour: "syncFinancialIntegrations",
          ref: "syncFinancialIntegrations",
        },
        interestEnabled && {
          title: "Generate Interest Charges",
          icon: faFileInvoiceDollar,
          onClick: generateInterestCharges,
          dataTour: "generateInterestCharges",
        },
        interestEnabled &&
          isStaff && {
            title: "Preview Interest Charges (Staff Only)",
            icon: faEye,
            onClick: previewInterestCharges,
            dataTour: "previewInterestCharges",
          },
        {
          title: `Bulk Edit (${selectedCount})`,
          icon: faPencil,
          isDisabled: selectedCount === 0,
          onClick: openEditBillingDocuments,
          dataTour: "bulkUpdate",
        },
        {
          title: "Generate Financial Reports",
          icon: faFiles,
          onClick:
            businessId && Boolean(formikProps)
              ? onClickGenerateBusinessReports
              : onClickGenerateFinancialStatements,
          dataTour: "generateFinancialStatements",
        },
        {
          isDisabled: selectedCount === 0,
          title: selectedCount
            ? `Compile (${selectedCount}) documents as PDF`
            : "Compile documents as PDF",
          icon: faFilePdf,
          onClick: compileSelected,
        },
      ].filter(Boolean),
    [
      businessId,
      compileSelected,
      formikProps,
      generateInterestCharges,
      handleDownloadOutstandingSettlementReport,
      integrationCredential,
      interestEnabled,
      isStaff,
      onClickGenerateBusinessReports,
      openEditBillingDocuments,
      previewInterestCharges,
      selectedCount,
      syncXeroDocuments,
    ],
  );

  const onRowSelectionChange = rows => {
    // This logic is already performed in the AgGridTable component :/
    setSelectedBillingDocumentIds(rows.map(r => r.billingDocument.id));
  };

  const columnDefs = useMemo(
    () => MoneyColumnDefinitions(interestEnabled),
    [interestEnabled],
  );

  // AG Grid v29 does not apply valueFormatter during CSV/Excel export; Date-typed valueGetters
  // produce Date objects that serialise as Date.toString(). Format them as YYYY-MM-DD.
  const exportProcessCellCallback = useCallback(params => {
    const { value } = params;
    if (value instanceof Date) {
      return formatISO8601DateString(value);
    }
    if (Array.isArray(value) && value.some(v => v instanceof Date)) {
      return value
        .filter(v => v instanceof Date)
        .map(formatISO8601DateString)
        .join(", ");
    }
    return value ?? "";
  }, []);

  const defaultCsvExportParams = useMemo(
    () => ({
      // only export selected rows to the export csv - if no rows are selected export all
      onlySelected: !!selectedBillingDocumentIds.length,
      processCellCallback: exportProcessCellCallback,
    }),
    [exportProcessCellCallback, selectedBillingDocumentIds.length],
  );

  const defaultExcelExportParams = useMemo(
    () => ({
      onlySelected: !!selectedBillingDocumentIds.length,
      processCellCallback: exportProcessCellCallback,
    }),
    [exportProcessCellCallback, selectedBillingDocumentIds.length],
  );

  const extraHeaderComponents = useMemo(
    () => (
      <>
        {loadAllLabel}
        {lastSync}
      </>
    ),
    [lastSync, loadAllLabel],
  );

  return (
    <Column fullHeight>
      {!businessId && (
        <Box p={2}>
          <TitleText>Money</TitleText>
        </Box>
      )}

      <AgGridTable
        defaultColDef={defaultColDef}
        columnDefs={columnDefs}
        context={context}
        extraActionComponents={extraHeaderComponents}
        getRowId={getRowId}
        rowData={rowData}
        additionalActions={additionalActions}
        onGridReady={onGridReady}
        tableName={tableName}
        detailCellRendererParams={detailCellRendererParams}
        onRowSelectionChanged={onRowSelectionChange}
        rowSelection={RowSelectionMode.MULTIPLE}
        masterDetail
        multiSelectActionOnly
        defaultCsvExportParams={defaultCsvExportParams}
        defaultExcelExportParams={defaultExcelExportParams}
        rowsSelectable
        exportSaleTitle={
          businessName ? `Transactions - ${businessName}` : "Transactions"
        }
      />
      {isEditBillingDocumentsOpen && (
        <BulkUpdateBillingDocumentModal
          onClose={closeEditBillingDocuments}
          billingDocumentIds={selectedBillingDocumentIds}
          initialValues={bulkInitialValues}
        />
      )}
    </Column>
  );
};

export const MoneyTableWrapper = ({ businessId, tableName }) => {
  const [allDocumentsLoaded, setAllDocumentsLoaded] = useState(false);

  const dispatch = useDispatch();

  const allSalesLoaded = useSelector(getSetting(Settings.allSalesLoaded));
  // We only want to fetch these once on mount, and outside of the <WaitFor> block to avoid rerenders forcing re-gets.
  useMountEffect(() => {
    if (businessId) {
      dispatch(
        BillingDocumentAction.request({
          extraQueryParams: {
            business__cbid: businessId,
          },
        }),
      );
    } else {
      dispatch(BillingRunAction.enable());
      dispatch(
        BillingDocumentAction.request({
          extraQueryParams: { recent_or_overdue: true },
        }),
      );
    }
    dispatch(PaymentAction.request({ onRecentOrOverdue: true }));
    if (!allSalesLoaded) {
      dispatch(setSetting(Settings.allSalesLoaded, true));
      dispatch(SaleAction.request({ fetchAll: true }));
    }
    return () => {
      dispatch(BillingRunAction.disable());
    };
  });

  return (
    <WaitForSync
      requiredData={[
        ApiModel.BILLING_DOCUMENTS,
        ApiModel.INTEGRATION_CREDENTIALS,
        ApiModel.SALES,
        ApiModel.PAYMENTS,
      ]}
    >
      <MoneyTable
        allDocumentsLoaded={allDocumentsLoaded}
        setAllDocumentsLoaded={setAllDocumentsLoaded}
        businessId={businessId}
        tableName={tableName}
      />
    </WaitForSync>
  );
};
